import { Component, OnDestroy, OnInit } from '@angular/core';
import { ServiceMessageComponent } from '@core/components/service-message/service-message.component';
import { DEFAULT_DIALOG_CONFIG, SereviceMessageType } from '@core/constants/serviceMessage.const';
import { StoreService } from '@core/store/store.service';
import { AgreementCreditsService } from '@module/agreement-credits/agreement-credits.service';
import { SubmissionManagementService } from '@module/submission-management/services/submission-management.service';
import { TranslateService } from '@ngx-translate/core';
import { BaseStepperStepComponent } from '@shared/components/base-stepper/base-stepper-step.component';
import { FormService } from '@shared/services/form.service';
import { ModalService } from '@shared/services/modal.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-review-activities',
  templateUrl: './review-activities.component.html',
  styleUrls: ['./review-activities.component.scss'],
})
export class ReviewActivitiesComponent extends BaseStepperStepComponent implements OnInit, OnDestroy {

  static key = 'ReviewActivitiesComponent';
  facilities: any[] = [];
  isOpa:boolean;
  headerDetails: any;
  openPanelStates: { [key: string]: boolean } = {}

  constructor(
    private modalService: ModalService,
    private storeService: StoreService,
    public formService: FormService,
    public service: AgreementCreditsService,
    private translate: TranslateService,
    private submissionManagementService: SubmissionManagementService
  ) {
    super();
    this.translate.onLangChange
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => this.onLangChange(),
      );
  }

  ngOnInit(): void {
    this.service.hideSaveAsDraft = true;
    this.inputData = this.service.inputData;
    if (!Array.isArray(this.inputData)) {
      this.inputData = [this.inputData];
    }
    
    const filteredOpa = this.inputData.find(record => record.opa === this.service.currentData.opaName);
    this.service.inputData = filteredOpa;
    
    this.isOpa = this.model.opaDetail && !this.model.opaDetail.editable &&
    this.model.opaDetail.id === this.storeService.user.associationsList[0].accountId;
    this.service.initModelChangeTracking();
    this.getOPAListItems();
  }

  getOPAListItems() {
    const payload = {
      id: this.service.currentData.id,
      complianceYear: this.model.complianceYear,
    };
    this.service.getAgreementOpaList(this.getRegistrationAccountId(), payload).pipe(
      takeUntil(this._destroy$),
    ).subscribe(
      response => {
        const facilities = response.flatMap(a => a.uniqueFacilities);
        const inputData = Array.isArray(this.inputData) ? this.inputData[0] : this.inputData;
        inputData.uniqueFacilities = this.updateUniqueFacilities(inputData.uniqueFacilities, facilities);
        this.facilities = inputData.uniqueFacilities;

        this.facilities.forEach(facility => {
          const panelId = facility.facilityId;
          this.openPanelStates[panelId] = false;
          facility.liquidFuelsList && facility.liquidFuelsList.sort();
          facility.gaseousFuelsList && facility.gaseousFuelsList.sort();
        });

        this.headerDetails = {
          type: inputData.uniqueFacilities[0].activity,
          legalName: inputData.uniqueFacilities[0].legalName || this.model.registrationAccount?.legalName,
          subType: inputData.uniqueFacilities[0].subAccountType,
          accountId: inputData.uniqueFacilities[0].accountId || this.model.registrationAccountId,
          registrationAccountId: inputData.uniqueFacilities[0].registrationAccountId || this.model.registrationReportAccountId,
        };

      });
}  

private updateUniqueFacilities(uniqueFacilities: any[], sourceFacilities?: any[]): any[] {
  const facilityTypeLabels = ['Residential', 'CSH', 'GPC'];

  return uniqueFacilities.map(dst => {
    const src = sourceFacilities?.find(f => f.id === dst.id) || dst;

    if (src?.liquidFuelsList) {
      dst.liquidFuelsList = [...src.liquidFuelsList].sort();
    }
    
    if (src?.gaseousFuelsList) {
      dst.gaseousFuelsList = [...src.gaseousFuelsList].sort();
    }

    if (facilityTypeLabels.includes(dst.type)) {
      dst.name = this.translate.instant(`REGISTRATION_PAGE.cfsRegistrationForm.${dst.type}`);
    }

    return dst;
  });
}

  onPanelShown(panelId: string) {
    this.openPanelStates[panelId] = true;
  }
  
  onPanelHidden(panelId: string) {
    this.openPanelStates[panelId] = false;
  }

  onLangChange(): void {
    const payload = {
      id: this.service.currentData.id,
      complianceYear: this.model.complianceYear,
    };
    this.service.getAgreementOpaList(this.getRegistrationAccountId(), payload).pipe(
      takeUntil(this._destroy$),
    ).subscribe(
      response => {
        const facilities = response.flatMap(a => a.uniqueFacilities);
        const inputData = Array.isArray(this.inputData) ? this.inputData[0] : this.inputData;
        inputData.uniqueFacilities =this.updateUniqueFacilities(inputData.uniqueFacilities, facilities);
        this.facilities = inputData.uniqueFacilities;
      });
  }

  showFuel(fuel:string){
    const tran = this.translate.instant(`COMMON.fuelList.${fuel}`);
    if(tran && tran.indexOf('COMMON.fuelList') === -1){
      return tran;
    }
    return fuel;
  }

  isActivityCodeERP(facility) {
    return this.checkFacility(facility, 'ERP');
  }

  isActivityCodeBPE(facility) {
    return this.checkFacility(facility, 'BPE');
  }


  isActivityCodePRODCI(facility) {
    return this.checkFacility(facility, 'PRODCI');
  }

  isActivityCodeFuelStation(facility) {
      const eUFS = this.checkFacility(facility, 'EUFS');
      const isTypeFS = facility.type === 'FuelingStation' ;

      return eUFS && isTypeFS;
    }

  isActivityCargingStation(facility) {
    const eUFS = this.checkFacility(facility, 'EUFS');
    const isTypeCS = (facility.type === 'NonResidential')  ;

    return eUFS && isTypeCS;
  }

  isActivityNRCargingStation(facility) {
    const eUFS = this.checkFacility(facility, 'EUFS');
    const isTypeCS =  (facility.type === 'Residential' || facility.type === 'CSH' || facility.type === 'GPC') ;

    return eUFS && isTypeCS;
  }
  checkFacility(facility, type: string) {
    const inputData = Array.isArray(this.inputData) ? this.inputData[0] : this.inputData;
    const facilityExist = inputData.uniqueFacilities.find(d => d.facilityId === facility.facilityId);
    if (facilityExist) {
      return facilityExist.activityCodeList && facilityExist.activityCodeList.find(element => element === type);
      }
    return false;
  }

  hasAddress(facility){
    return !this.isActivityCodeERP(facility) && !this.isActivityNRCargingStation(facility);
  }

  saveAndProceed(draft = false) {
    if(this.checkForDisableState() && this.service.currentData.opaDetail && !this.service.currentData.opaDetail.editable){
      this.modalService
      .open(
          ServiceMessageComponent,
          {
              messages: null,
              message: 'opaExistsMessage',
              type: SereviceMessageType.WARNING,
          },
          true,
          DEFAULT_DIALOG_CONFIG,
      )
      .afterClosed()
      .subscribe((result?: any) => {
          if (result) {
            this.service.nextStep();
          }
      });
    } else  {
      this.service.nextStep();
    }
  }

  private getRegistrationAccountId() {
    return this.service.currentData.registrationAccountId ? this.service.currentData.registrationAccountId : this.service.currentData.registrationAccount.id;
  }

  ngOnDestroy() {
    this._destroy$.next(null);
    this._destroy$.complete();
    this.service.hideSaveAsDraft = false;
  }
}
